// Copyright (c) Hamilton Manufacturing Corp.
import React from 'react';
import styled from 'styled-components';
import AccountForm from '../../_common/AccountForm';
import { Regex } from '../../_common/api/regex';
import Cart from '../../_common/Cart';
import Button from '../../_common/form/Button';
import Form from '../../_common/form/Form';
import Input from '../../_common/form/Input';
import { useGlobalState } from '../../_common/globalState/GlobalStateProvider';
import Modal from '../../_common/Modal';
import UpdatePassword from '../../_common/UpdatePassword';
import Card from '../Card';
import MenuRoutes from '../MenuRoutes';
import Options from '../Options';
import { QuestRoute } from '../../_common/api/QuestRoutes';
import { clearAllPersistedState } from '../../_common/api/usePersistedState';
import { HamQuest } from '../../../App';
import Loading from '../../_common/Loading';
import Select from '../../_common/form/Select';

const Row = styled.div`
    display: flex;
    justify-content: space-between;
`
const Terms = styled.div`
    font-size: 0.6em;
    margin: 0.6em 0;
`
export const DeleteButton = styled(Button)`
    background: ${({ theme }) => theme.error}
`

const Account = ({ ...props }) => {
    const {
        state: {
            active_user,
            site_config,
            version
        },
        logOut,
        getVehicle
    } = useGlobalState();

    const vehiclePopulated = () => {
        if(active_user.vehicle.make && active_user.vehicle.model && active_user.vehicle.year && active_user.vehicle.color && active_user.vehicle.license){
            return <Terms>Reach out to support to change your vehicle information.</Terms>
        }else{
            return <Button onClick={addVehicle}>Add Vehicle Info</Button>
        }
    }

    const addVehicle = () => {
        Modal.Show({
            title: 'Vehicle Info',
            body: (
                <Form
                    inputs={{
                        make: '',
                        model: '',
                        year: '',
                        license: '',
                        color: '',
                        state: '',
                    }}
                    onSubmit={e =>
                        Modal.Promise({
                            title: 'Saving Vehicle',
                            body: <Loading />,
                            callback: HamQuest.post(QuestRoute.user.vehicle, e)
                        })
                            .then(() => {
                                Modal.Close();
                                getVehicle();
                            })
                    }
                >
                    <Select
                        label='State'
                        name='state'
                        required
                        options={
                            [
                                {
                                    value: 0,
                                    label: '-'
                                },
                                ...site_config.states
                            ]
                        }
                    />
                    <Input
                        name='make'
                        label='Make'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='model'
                        label='Model'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='color'
                        label='Color'
                        maxLength={20}
                        required
                    />
                    <Input
                        name='year'
                        label='Year'
                        sanitize={Regex.match.notNumbers}
                        numpad
                        maxLength={4}
                        required
                    />
                    <Input
                        name='license'
                        label='License Plate'
                        required
                        maxLength={10}
                        sanitize={Regex.match.specialChar}
                        validate={/(\w|\d)+/g}
                        mask={(e) => e.toUpperCase()}
                        tag='Used for License Plate Reading'
                    />
                </Form>
            )
        })
    }

    const handleDelete = () => {
        Modal.Confirmation({
            title: 'Hold On!',
            type: 'warning',
            body: `Are you sure you want to delete your account?`,
            hideClose: true,
            closeAfter: true,
        })
            .then(res => {
                if (!res) return;
                Modal.Confirmation({
                    title: 'LAST CHANCE!',
                    type: 'error',
                    body: `Are you sure you want to delete your account? This can not be undone! You will lose access to any and all unredeemed items.`,
                    hideClose: true,
                    closeAfter: true,
                })
                    .then(res => {
                        if (!res) return;
                        HamQuest.delete(QuestRoute.user.delete)
                            .then(() => {
                                Cart.Empty();
                                logOut();
                            })
                    })
            })
    }

    const handlePWUpdate = () => {
        Modal.Show({
            title: 'Update password',
            icon: 'lock',
            body: <UpdatePassword />
        })
    }

    const account = {
        firstName: active_user.account.firstName,
        lastName: active_user.account.lastName,
        email: active_user.account.email,
        confirmEmail: active_user.account.email,
        birthdayMonth: active_user.account.birthdayMonth,
        birthdayDay: active_user.account.birthdayDay,
        cellPhoneNumber: active_user.account.cellPhoneNumber,
        zipPostalCode: active_user.account.zipPostalCode,
        optInToMarketing: active_user.account.optInToMarketing,
    }

    const routes = MenuRoutes();

    return (
        <>
            <Card title='My Account' icon='user' collapsible>
                <AccountForm
                    inputs={account}
                    submitButton='Update'
                    submittingButton='Updating'
                    displayform
                />
            </Card>

            {
                active_user.vehicle ?
                    <Card title='Vehicle Info' icon='car' collapsible>
                        <Input
                            standalone
                            label='Make'
                            value={active_user.vehicle.make}
                            disabled
                        />
                        <Input
                            standalone
                            label='Model'
                            value={active_user.vehicle.model}
                            disabled
                        />
                        <Input
                            standalone
                            label='Year'
                            value={active_user.vehicle.year}
                            disabled
                        />
                        <Input
                            standalone
                            label='Color'
                            value={active_user.vehicle.color}
                            disabled
                        />
                        <Input
                            standalone
                            label='License Plate'
                            value={active_user.vehicle.license}
                            disabled
                        />
                        {vehiclePopulated()}
                    </Card> : null
            }

            <Card title='Payment Info' icon='creditCard' collapsible>
                {
                    // HERE 
                    active_user.payment.onFile ?
                        <>
                            <Input
                                standalone
                                label='Card Type'
                                value={active_user.payment.cardType}
                                disabled
                            />
                            <Input
                                standalone
                                label='Card Number'
                                value={'XXXX-XXXX-XXXX-' + active_user.payment.maskedCardNumber.match(/\d+/g)[0]}
                                disabled
                            />
                            <Input
                                standalone
                                label='Expiration'
                                value={`${active_user.payment.expirationMonth} / ${active_user.payment.expirationYear}`}
                                disabled
                            />
                        </> : <div>None on file</div>
                }
                <Button onClick={() => Cart.AddCardToAccount()}>{active_user.payment ? 'Update Card' : 'Add Card'}</Button>
            </Card>

            <Options />

            <Card>
                <Button onClick={handlePWUpdate}>Update Password</Button>
                <Button to={routes.purchaseHistory.route}>My Purchases</Button>
                <Button to={routes.support.route}>Support</Button>
                <Button to={routes.termsAndConditions.route}>Terms & Conditions</Button>
                <Button to={routes.privacyPolicy.route}>Privacy Policy</Button>

                {!active_user.account.emailConfirmed &&
                    <Button onClick={() => {
                        Modal.Show({
                            title: 'Email Verification',
                            body: `Would you like to send a confirmation email to ${active_user.account.email}. Please verify your email to complete your account setup.`,
                            buttons: <Button
                                onClick={() =>
                                    Modal.Promise({
                                        title: 'Sending a new Email',
                                        callback: HamQuest.get(QuestRoute.user.confirmationEmail),
                                        body: <Loading />
                                    })
                                        .then(() => Modal.Show({
                                            title: 'Success',
                                            body: `An new email has been sent to ${active_user.account.email}. Please verify your email to complete your account setup.`,
                                            type: 'success'
                                        }))
                                        .catch((er) => Modal.Show({
                                            title: 'Error',
                                            body: `An error occurred when sending an email to ${active_user.account.email}.`,
                                            type: 'error'
                                        }))
                                }
                            >
                                Resend Email
                            </Button>
                        })
                    }}>Re-send Email Confirmation</Button>
                }

                <Button onClick={async () => {
                    await clearAllPersistedState();
                    window.location.reload();
                }}>Reload App</Button>
                <Button
                    to='/logOut'
                    onClick={() => {
                        logOut();
                        Cart.Empty();
                    }}
                >Log Out</Button>
                <DeleteButton
                    onClick={handleDelete}
                >Delete Account</DeleteButton>
                <Input
                    standalone
                    value={site_config.timeStamp}
                    label='Merchant Version Control'
                    disabled
                />
                <Input
                    standalone
                    value={version}
                    label='App Version'
                    disabled
                />
            </Card>
        </>
    );
}

export default Account;